import {
  MyProperty,
  Image,
  LeadContact,
  LeadContactWithPermissions,
  PredictedPrice,
  PropertyImage,
  PropertyPhotoCategory,
} from "common/types/common.types";
import {
  DecodedAppraiserToken,
  PropertyDeliveryTimeType,
  PropertyType,
} from "./api.types";

export enum AppraiserRole {
  ADMIN = "ADMIN",
  APPRAISER = "APPRAISER",
  SPECTATOR = "SPECTATOR",
  DATA_ENTRY = "DATA_ENTRY",
  ORGANIZATION_LEAD = "ORGANIZATION_LEAD",
}

/** Roles that can use most of the functionalities. Does not include partial roles like spectator, data entry */
export const DefaultRoles = [
  AppraiserRole.ADMIN,
  AppraiserRole.APPRAISER,
  AppraiserRole.ORGANIZATION_LEAD,
];

export interface RC_EndUserInfo {
  vv: string;
  vaidmuo: string;
  id: string;
}

export interface RcCredentialsRequest {
  RC_EndUserInfo: RC_EndUserInfo;
  RC_IsProduction: boolean;
}

export interface Appraiser extends BasicAppraiser {
  _id: string;
  invitedAt?: Date;
  acceptedAt?: Date;
  deletedAt?: Date;
  lastActiveAt?: Date;
  registrationLink?: string;
  resetLink?: string | null;
  decodedRegistrationToken?: DecodedAppraiserToken;
  decodedPasswordResetToken?: DecodedAppraiserToken;
  role: AppraiserRole;

  RC_EndUserInfo?: RC_EndUserInfo;
  RC_IsProduction?: boolean;
  useOrganizationCredentials?: boolean;
  createdAt: Date;
  updatedAt: Date;
  isReloAppraiser?: boolean;
}

export interface BasicAppraiser extends AppraiserProfile {
  email: string;
  organizationName: string;
}

export enum PropertyRequestStatus {
  Created = "created",
  Pending = "pending",
  InProgress = "inProgress",
  Evaluated = "evaluated",
  Completed = "completed",
}

export enum PropertyRequestType {
  Manual = "manual",
  LeadAppraiser = "leadAppraiser",
  LeadOrganization = "leadOrganization",
}
export interface PropertyStatus {
  status: PropertyRequestStatus;
  createdAt: Date;
}

export enum ClimateRiskType {
  None = "None",
  Low = "Low",
  Medium = "Medium",
  High = "High",
}

export interface PropertyRisk {
  year: number;
  cityLocativeName: string;
  risk: ClimateRiskType;
}

export interface PropertyFloodRisk {
  floodRisk: ClimateRiskType | null;
}

export interface PropertyErosionScores {
  rcp26: number;
  rcp45: number;
  rcp85: number;
}

export interface PropertyErosionRisk {
  erosionRisk: ClimateRiskType | null;
  erosionScores?: PropertyErosionScores;
  cadastralArea?: string;
  municipality?: string;
}

export interface PropertyHeatRisk extends PropertyRisk {
  hotDaysCount: number;
}

export interface PropertyWindRisk extends PropertyRisk {
  windyDaysCount: number;
}

export interface PropertyAirPollutionRisk extends PropertyRisk {
  pm_10: number;
}

export interface NoisePollutionLevel {
  minLevel: number;
  maxLevel?: number | null;
}

export interface PropertyNoisePollutionRisk extends Omit<PropertyRisk, "risk"> {
  risk?: ClimateRiskType;
  minLevel?: number;
  maxLevel?: number | null;
  levels?: NoisePollutionLevel[];
  industryRisk?: ClimateRiskType;
  industryMinLevel?: number;
  industryMaxLevel?: number | null;
  industryLevels?: NoisePollutionLevel[];
  queryMethod?: "plot" | "radius";
}

export interface Order {
  _id: string;
  discountCode?: string | null;
  deliveryTime?: PropertyDeliveryTimeType;
  deliveryTimeLabel?: string | null;
  deliveryDate?: string | null;
}

export interface PhotoCategory {
  category: PropertyPhotoCategory;
  propertyId: string;
  description?: string;
}

export enum PropertyPurpose {
  Pledge = "pledge",
  Consultative = "consultative",
}

export const PropertyPurposeTranslations: Record<PropertyPurpose, string> = {
  [PropertyPurpose.Pledge]: "Įkeitimas",
  [PropertyPurpose.Consultative]: "Konsultacinis",
};

export interface AppraiserProperty extends MyProperty {
  appraiser: Appraiser;
  finalPredictionCreatedAt?: string;
  images: PropertyImage[];
  shareableLink?: string;
  leadContact?: LeadContactWithPermissions;
  propertyStatuses: PropertyStatus[];
  propertyRequestStatus: PropertyRequestStatus;
  propertyRequestType?: PropertyRequestType;
  order?: Order | null;
  simpleType?: PropertyType;
  reportVerifiedAt?: Date;
  listingUrl?: string;
  photoCategories?: PhotoCategory[];
  purpose?: PropertyPurpose;
  updatedAtWithToken?: string;
  transactionId?: string;
  floodRisk?: ClimateRiskType | null;
  erosionRisk?: PropertyErosionRisk;
  heatRisk?: PropertyHeatRisk;
  windRisk?: PropertyWindRisk;
  airPollutionRisk?: PropertyAirPollutionRisk;
  noisePollutionRisk?: PropertyNoisePollutionRisk;
  reportNumber?: number;
  inPortfolio?: boolean;
  predicted_price?: PredictedPrice;
}

export interface AppraiserWithLimits {
  _id: string;
  email: string;
  monthlyCreditLimit?: number | null;
  totalCreditsUsed: number;
}

export interface AppraiserProfile {
  profileImageUrl: string;
  fullName: string;
  positionTitle: string;
  phoneNumber: string;
  website?: string;
  certificateNumber?: string;
  certificateValidFrom?: Date | null;
  isReloAppraiser?: boolean;
}

export interface AppraiserLoginInfo {
  _id: string;
  appraiserId: string;
  ipAddress: string;
  device: string;
  os: string;
  browser: string;
  location: string;
  deviceFingerprint: string;
  loggedInBy?: string;
  loginReason?: string;
  createdAt: Date;
  updatedAt: Date;
}
